import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import BusinessHoursEditor from './BusinessHoursEditor';
import { getDefaultBusinessHours, generateBusinessHoursText } from '../../utils/businessHoursHelpers';
import { limitesDelConocimiento } from '../../utils/limitesDelConocimiento';

export default function KnowledgeForm({ knowledge, onChange }) {
  const { t } = useTranslation('bots');
  const [restrictedInput, setRestrictedInput] = useState('');

  // Initialize knowledge with defaults, merging with existing data
  const safeKnowledge = {
    company_name: '',
    website: '',
    phone: '',
    email: '',
    address: '',
    business_hours: '',
    instagram: '',
    facebook: '',
    linkedin: '',
    company_info: '',
    products_services: '',
    faqs: '',
    policies: '',
    restricted_info: [],
    conversation_examples: [],
    // Merge with existing knowledge, giving priority to existing values
    ...knowledge,
  };

  /**
   * EL HORARIO QUE LA PANTALLA MUESTRA CUANDO NO HAY NINGUNO GUARDADO.
   *
   * ⚠️ Se calcula UNA VEZ y se usa en los dos sitios —lo que ve el editor y lo que se
   * emite hacia arriba— a propósito. Si el render lo sintetizara por su cuenta y el
   * emisor lo sintetizara otra vez, serían dos objetos distintos que hoy coinciden y
   * mañana no.
   */
  const horarioMostrado = safeKnowledge.business_hours_structured || getDefaultBusinessHours();

  /**
   * ⚠️ LA PANTALLA GUARDA LO QUE ENSEÑA — y esto arregla un defecto real (24-sep-2026).
   *
   * EL CASO: el editor se montaba con `business_hours_structured || getDefaultBusinessHours()`,
   * y ese default es EXACTAMENTE «lunes a viernes 09:00–18:00, sábado y domingo cerrados».
   * Rodrigo abrió la pestaña para configurar justo ese horario, lo vio ya en pantalla, no
   * tocó nada y pulsó Guardar. `BusinessHoursEditor` sólo emite `onChange` al interactuar
   * —su `useEffect` hace `setSchedule` y nada más—, así que el campo nunca entró en el
   * estado, nunca viajó en el POST y nunca se guardó.
   *
   * El guardado fue un éxito: 16 claves escritas, `company_name` y `website` incluidos.
   * Sólo faltaba la que él creía haber configurado. **Un formulario que enseña un valor
   * por defecto que no persiste es peor que uno vacío**: el vacío se ve, esto no.
   *
   * Lo confirmó el discriminante del texto: `handleBusinessHoursChange` escribe LOS DOS
   * campos —el estructurado y el texto legible— así que con los dos vacíos la única
   * explicación posible es que nunca disparó.
   *
   * POR QUÉ AL MONTAR Y NO AL GUARDAR: el botón de guardar vive en `BotConfig`, que no
   * sabe nada de horarios y no debería aprenderlo. Y por qué SÓLO cuando falta: emitir
   * siempre pisaría el horario del usuario en cada montaje.
   *
   * SÍ, ENCIENDE EL AVISO DE «CAMBIOS SIN GUARDAR» al abrir la pestaña, y es correcto:
   * hay algo en pantalla que no está guardado. Ocurre UNA VEZ por bot; en cuanto se
   * guarda, el campo existe y esto no vuelve a dispararse.
   */
  const yaEmitido = useRef(false);
  useEffect(() => {
    if (yaEmitido.current) return;
    if (knowledge && knowledge.business_hours_structured) return;
    yaEmitido.current = true;
    onChange({
      ...safeKnowledge,
      business_hours_structured: horarioMostrado,
      business_hours: safeKnowledge.business_hours || generateBusinessHoursText(horarioMostrado),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [knowledge]);

  const updateField = (field, value) => {
    onChange({ ...safeKnowledge, [field]: value });
  };

  // Handle business hours update - generate text automatically
  const handleBusinessHoursChange = (newHours) => {
    const businessHoursText = generateBusinessHoursText(newHours);
    onChange({
      ...safeKnowledge,
      business_hours_structured: newHours,
      business_hours: businessHoursText
    });
  };

  // Restricted info handlers
  const addRestrictedInfo = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const value = restrictedInput.replace(/,/g, '').trim();
      if (value) {
        const currentRestricted = safeKnowledge.restricted_info || [];
        if (!currentRestricted.includes(value)) {
          updateField('restricted_info', [...currentRestricted, value]);
        }
        setRestrictedInput('');
      }
    }
  };

  const removeRestrictedInfo = (item) => {
    const updated = (safeKnowledge.restricted_info || []).filter(r => r !== item);
    updateField('restricted_info', updated);
  };

  // Conversation examples handlers
  const addExample = () => {
    const currentExamples = safeKnowledge.conversation_examples || [];
    updateField('conversation_examples', [
      ...currentExamples,
      { user: '', bot: '' }
    ]);
  };

  const updateExample = (index, field, value) => {
    const updated = [...(safeKnowledge.conversation_examples || [])];
    updated[index][field] = value;
    updateField('conversation_examples', updated);
  };

  const removeExample = (index) => {
    const updated = (safeKnowledge.conversation_examples || []).filter((_, i) => i !== index);
    updateField('conversation_examples', updated);
  };

  // LOS LÍMITES DEL BOT (15-oct-2026, `utils/limitesDelConocimiento.js`). Antes cada sección
  // era un textarea con `maxLength={5000}`: el navegador cortaba en silencio lo que se pegaba de
  // más (las políticas de Bigwaves perdieron la garantía y el contacto) y el contador decía
  // «5000 / 5000». Ahora el texto entero queda a la vista, el contador mide como mide el bot y
  // avisa en rojo cuánto queda fuera; el guardado pide confirmación (BotConfig).
  const limites = limitesDelConocimiento(safeKnowledge);
  // La sección donde se acaba de pegar un texto que deja la sección por encima del tope.
  const [pegadoDeMas, setPegadoDeMas] = useState(null);
  const pegandoEn = useRef(null);
  const alEditarSeccion = (campo, valor) => {
    const vieneDePegar = pegandoEn.current === campo;
    pegandoEn.current = null;
    if (vieneDePegar || pegadoDeMas === campo) {
      const excede = limitesDelConocimiento({ ...safeKnowledge, [campo]: valor }).secciones[campo].estado === 'excede';
      if (vieneDePegar) setPegadoDeMas(excede ? campo : null);
      else if (!excede) setPegadoDeMas(null);
    }
    updateField(campo, valor);
  };

  return (
    <div className="knowledge-form">
      <div className="form-header">
        <h3>{t('knowledge.title')}</h3>
        <p className="subtitle">
          {t('knowledge.subtitle')}
        </p>
        <div className="char-counter" style={estiloContador(limites.total.estado)}>
          {t('knowledge.charCounter', { current: limites.total.largo, max: limites.total.max })}
        </div>
        {limites.total.estado === 'excede' && (
          <p role="alert" style={estiloAviso('excede')}>
            {t('knowledge.limites.totalExcede', { max: limites.total.max, sobra: limites.total.sobra })}
          </p>
        )}
      </div>

      {/* Basic Company Information Section */}
      <div className="form-section">
        <h4 className="section-title">{t('knowledge.basicInfoTitle')}</h4>
        <p className="section-subtitle">
          {t('knowledge.basicInfoSubtitle')}
        </p>

        <div className="form-row">
          <div className="form-field">
            <label htmlFor="company_name">{t('knowledge.companyName')}</label>
            <input
              type="text"
              id="company_name"
              value={safeKnowledge.company_name || ''}
              onChange={(e) => updateField('company_name', e.target.value)}
              placeholder={t('knowledge.companyNamePlaceholder')}
              maxLength={100}
            />
          </div>

          <div className="form-field">
            <label htmlFor="website">{t('knowledge.website')}</label>
            <input
              type="url"
              id="website"
              value={safeKnowledge.website || ''}
              onChange={(e) => updateField('website', e.target.value)}
              placeholder={t('knowledge.websitePlaceholder')}
              maxLength={200}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label htmlFor="phone">{t('knowledge.phone')}</label>
            <input
              type="tel"
              id="phone"
              value={safeKnowledge.phone || ''}
              onChange={(e) => updateField('phone', e.target.value)}
              placeholder={t('knowledge.phonePlaceholder')}
              maxLength={50}
            />
          </div>

          <div className="form-field">
            <label htmlFor="email">{t('knowledge.email')}</label>
            <input
              type="email"
              id="email"
              value={safeKnowledge.email || ''}
              onChange={(e) => updateField('email', e.target.value)}
              placeholder={t('knowledge.emailPlaceholder')}
              maxLength={100}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-field full-width">
            <label htmlFor="address">{t('knowledge.address')}</label>
            <input
              type="text"
              id="address"
              value={safeKnowledge.address || ''}
              onChange={(e) => updateField('address', e.target.value)}
              placeholder={t('knowledge.addressPlaceholder')}
              maxLength={200}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-field full-width">
            <label>{t('knowledge.businessHours')}</label>
            {/* LA FRASE QUE SEPARA LOS DOS HORARIOS. El mismo `BusinessHoursEditor` se
                usa aquí y en «Disponibilidad para reuniones», con la misma forma y dos
                significados distintos — y nada en la interfaz lo decía. Costó que el bot
                prometiera contacto humano a una hora en la que ya no hay nadie. */}
            <p style={{
              margin: '0 0 0.6rem', fontSize: '13px', lineHeight: 1.45, color: '#4a5568',
            }}>
              {t('knowledge.businessHoursHint')}
            </p>
            {safeKnowledge.business_hours && !safeKnowledge.business_hours_structured && (
              <div className="migration-notice">
                {t('knowledge.migrationNotice')}
                <br />
                <strong>{t('knowledge.previousFormat')}</strong> "{safeKnowledge.business_hours}"
              </div>
            )}
            <BusinessHoursEditor
              value={safeKnowledge.business_hours_structured || getDefaultBusinessHours()}
              onChange={handleBusinessHoursChange}
              timezone={safeKnowledge.timezone || 'America/Santiago'}
            />
          </div>
        </div>

        <div className="social-media-section">
          <h5 className="subsection-title">{t('knowledge.socialMedia')}</h5>
          <div className="form-row">
            <div className="form-field">
              <label htmlFor="instagram">{t('knowledge.instagram')}</label>
              <input
                type="text"
                id="instagram"
                value={safeKnowledge.instagram || ''}
                onChange={(e) => updateField('instagram', e.target.value)}
                placeholder={t('knowledge.igPlaceholder')}
                maxLength={100}
              />
            </div>

            <div className="form-field">
              <label htmlFor="facebook">{t('knowledge.facebook')}</label>
              <input
                type="text"
                id="facebook"
                value={safeKnowledge.facebook || ''}
                onChange={(e) => updateField('facebook', e.target.value)}
                placeholder={t('knowledge.fbPlaceholder')}
                maxLength={100}
              />
            </div>

            <div className="form-field">
              <label htmlFor="linkedin">{t('knowledge.linkedin')}</label>
              <input
                type="text"
                id="linkedin"
                value={safeKnowledge.linkedin || ''}
                onChange={(e) => updateField('linkedin', e.target.value)}
                placeholder={t('knowledge.linkedinPlaceholder')}
                maxLength={100}
              />
            </div>

            <div className="form-field">
              <label htmlFor="tiktok">{t('knowledge.tiktok')}</label>
              <input
                type="text"
                id="tiktok"
                value={safeKnowledge.tiktok || ''}
                onChange={(e) => updateField('tiktok', e.target.value)}
                placeholder={t('knowledge.tiktokPlaceholder')}
                maxLength={100}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Company Info */}
      <div className="form-group">
        <label htmlFor="company_info">
          {t('knowledge.companyInfo')}
          <span className="char-count" style={estiloContador(limites.secciones.company_info.estado)}>
            {limites.secciones.company_info.largo} / {limites.secciones.company_info.max}
          </span>
        </label>
        <textarea
          id="company_info"
          value={safeKnowledge.company_info || ''}
          onPaste={() => { pegandoEn.current = 'company_info'; }}
          onChange={(e) => alEditarSeccion('company_info', e.target.value)}
          placeholder={t('knowledge.companyInfoPlaceholder')}
          rows={5}
          aria-describedby="limite-company_info"
        />
        <AvisoDeLimite id="limite-company_info" seccion={limites.secciones.company_info} pegado={pegadoDeMas === 'company_info'} t={t} />
        <p className="help-text">
          {t('knowledge.companyInfoHelp')}
        </p>
      </div>

      {/* Products & Services */}
      <div className="form-group">
        <label htmlFor="products_services">
          {t('knowledge.productsServices')}
          <span className="char-count" style={estiloContador(limites.secciones.products_services.estado)}>
            {limites.secciones.products_services.largo} / {limites.secciones.products_services.max}
          </span>
        </label>
        <textarea
          id="products_services"
          value={safeKnowledge.products_services || ''}
          onPaste={() => { pegandoEn.current = 'products_services'; }}
          onChange={(e) => alEditarSeccion('products_services', e.target.value)}
          placeholder={t('knowledge.productsServicesPlaceholder')}
          rows={5}
          aria-describedby="limite-products_services"
        />
        <AvisoDeLimite id="limite-products_services" seccion={limites.secciones.products_services} pegado={pegadoDeMas === 'products_services'} t={t} />
        <p className="help-text">
          {t('knowledge.productsServicesHelp')}
        </p>
      </div>

      {/* FAQs */}
      <div className="form-group">
        <label htmlFor="faqs">
          {t('knowledge.faqs')}
          <span className="char-count" style={estiloContador(limites.secciones.faqs.estado)}>
            {limites.secciones.faqs.largo} / {limites.secciones.faqs.max}
          </span>
        </label>
        <textarea
          id="faqs"
          value={safeKnowledge.faqs || ''}
          onPaste={() => { pegandoEn.current = 'faqs'; }}
          onChange={(e) => alEditarSeccion('faqs', e.target.value)}
          placeholder={t('knowledge.faqsPlaceholder')}
          rows={5}
          aria-describedby="limite-faqs"
        />
        <AvisoDeLimite id="limite-faqs" seccion={limites.secciones.faqs} pegado={pegadoDeMas === 'faqs'} t={t} />
        <p className="help-text">
          {t('knowledge.faqsHelp')}
        </p>
      </div>

      {/* Policies */}
      <div className="form-group">
        <label htmlFor="policies">
          {t('knowledge.policies')}
          <span className="char-count" style={estiloContador(limites.secciones.policies.estado)}>
            {limites.secciones.policies.largo} / {limites.secciones.policies.max}
          </span>
        </label>
        <textarea
          id="policies"
          value={safeKnowledge.policies || ''}
          onPaste={() => { pegandoEn.current = 'policies'; }}
          onChange={(e) => alEditarSeccion('policies', e.target.value)}
          placeholder={t('knowledge.policiesPlaceholder')}
          rows={5}
          aria-describedby="limite-policies"
        />
        <AvisoDeLimite id="limite-policies" seccion={limites.secciones.policies} pegado={pegadoDeMas === 'policies'} t={t} />
        <p className="help-text">
          {t('knowledge.policiesHelp')}
        </p>
      </div>

      {/* Restricted Info */}
      <div className="form-group">
        <label>
          {t('knowledge.restrictedInfo')}
        </label>
        <p className="help-text">
          {t('knowledge.restrictedInfoHelp')}
        </p>
        <div className="chip-input-container">
          <div className="chips-list">
            {(safeKnowledge.restricted_info || []).map((item, idx) => (
              <div key={idx} className="chip">
                <span>{item}</span>
                <button
                  type="button"
                  onClick={() => removeRestrictedInfo(item)}
                  className="chip-remove"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <input
            type="text"
            value={restrictedInput}
            onChange={(e) => setRestrictedInput(e.target.value)}
            onKeyDown={addRestrictedInfo}
            placeholder={t('knowledge.restrictedInfoPlaceholder')}
            className="chip-input"
          />
        </div>
        <p className="help-text-small">
          {t('knowledge.restrictedInfoExamples')}
        </p>
      </div>

      {/* Conversation Examples */}
      <div className="form-group">
        <label>
          {t('knowledge.conversationExamples')}
        </label>
        <p className="help-text">
          {t('knowledge.conversationExamplesHelp')}
        </p>

        <div className="examples-list">
          {(safeKnowledge.conversation_examples || []).map((example, idx) => (
            <div key={idx} className="example-item">
              <div className="example-header">
                <h4>{t('knowledge.exampleNumber', { number: idx + 1 })}</h4>
                <button
                  type="button"
                  onClick={() => removeExample(idx)}
                  className="btn-remove"
                >
                  {t('knowledge.delete')}
                </button>
              </div>
              <div className="example-fields">
                <div className="example-field">
                  <label>{t('knowledge.userSays')}</label>
                  <textarea
                    value={example.user || ''}
                    onChange={(e) => updateExample(idx, 'user', e.target.value)}
                    placeholder={t('knowledge.userSaysPlaceholder')}
                    rows={2}
                    maxLength={500}
                  />
                </div>
                <div className="example-field">
                  <label>{t('knowledge.botReplies')}</label>
                  <textarea
                    value={example.bot || ''}
                    onChange={(e) => updateExample(idx, 'bot', e.target.value)}
                    placeholder={t('knowledge.botRepliesPlaceholder')}
                    rows={3}
                    maxLength={500}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {(safeKnowledge.conversation_examples || []).length < 6 && (
          <button
            type="button"
            onClick={addExample}
            className="btn-add-example"
          >
            {t('knowledge.addExample')}
          </button>
        )}
      </div>

      <style>{`
        .knowledge-form {
          max-width: 900px;
        }

        .form-header {
          margin-bottom: 2rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid #e2e8f0;
        }

        .form-header h3 {
          margin: 0 0 0.5rem 0;
          font-size: 1.5rem;
          color: #2d3748;
        }

        .subtitle {
          color: #718096;
          font-size: 0.95rem;
          margin: 0 0 1rem 0;
        }

        .char-counter {
          display: inline-block;
          padding: 0.5rem 1rem;
          background: #e6fffa;
          border: 1px solid #81e6d9;
          border-radius: 6px;
          font-size: 0.875rem;
          color: #234e52;
          font-weight: 600;
        }

        .char-counter.warning {
          background: #fff5f5;
          border-color: #fc8181;
          color: #c53030;
        }

        .form-group {
          margin-bottom: 2rem;
        }

        .form-group label {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
          font-weight: 600;
          font-size: 1rem;
          color: #2d3748;
        }

        .char-count {
          font-size: 0.75rem;
          color: #a0aec0;
          font-weight: 400;
        }

        .form-group textarea {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid #cbd5e0;
          border-radius: 6px;
          font-size: 0.95rem;
          font-family: inherit;
          resize: vertical;
          transition: border-color 0.2s;
        }

        .form-group textarea:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .help-text {
          margin: 0.5rem 0 0 0;
          font-size: 0.85rem;
          color: #718096;
          font-style: italic;
        }

        .help-text-small {
          margin: 0.5rem 0 0 0;
          font-size: 0.75rem;
          color: #a0aec0;
          font-style: italic;
        }

        .chip-input-container {
          border: 1px solid #cbd5e0;
          border-radius: 6px;
          padding: 0.5rem;
          background: white;
        }

        .chips-list {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
        }

        .chip {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.375rem 0.75rem;
          background: #edf2f7;
          border: 1px solid #cbd5e0;
          border-radius: 16px;
          font-size: 0.875rem;
          color: #2d3748;
        }

        .chip-remove {
          background: none;
          border: none;
          color: #a0aec0;
          font-size: 1.25rem;
          cursor: pointer;
          padding: 0;
          line-height: 1;
          transition: color 0.2s;
        }

        .chip-remove:hover {
          color: #fc8181;
        }

        .chip-input {
          width: 100%;
          border: none;
          padding: 0.5rem;
          font-size: 0.95rem;
          outline: none;
        }

        .examples-list {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          margin: 1rem 0;
        }

        .example-item {
          padding: 1.5rem;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          background: #f7fafc;
        }

        .example-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .example-header h4 {
          margin: 0;
          font-size: 1rem;
          color: #2d3748;
        }

        .btn-remove {
          padding: 0.375rem 0.75rem;
          background: white;
          border: 1px solid #fc8181;
          color: #c53030;
          border-radius: 6px;
          font-size: 0.875rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-remove:hover {
          background: #fff5f5;
        }

        .example-fields {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .example-field label {
          display: block;
          margin-bottom: 0.5rem;
          font-size: 0.875rem;
          font-weight: 600;
          color: #4a5568;
        }

        .example-field textarea {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid #cbd5e0;
          border-radius: 6px;
          font-size: 0.9rem;
          font-family: inherit;
          background: white;
        }

        .example-field textarea:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .btn-add-example {
          width: 100%;
          padding: 0.75rem;
          background: white;
          border: 2px dashed #cbd5e0;
          color: #667eea;
          border-radius: 8px;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-add-example:hover {
          background: #f7fafc;
          border-color: #667eea;
        }

        /* New Company Basic Info Styles */
        .form-section {
          margin-bottom: 3rem;
          padding: 2rem;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          background: #f7fafc;
        }

        .section-title {
          margin: 0 0 0.5rem 0;
          font-size: 1.25rem;
          color: #2d3748;
          font-weight: 700;
        }

        .section-subtitle {
          margin: 0 0 1.5rem 0;
          color: #718096;
          font-size: 0.9rem;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .form-field {
          display: flex;
          flex-direction: column;
        }

        .form-field.full-width {
          grid-column: 1 / -1;
        }

        .form-field label {
          margin-bottom: 0.5rem;
          font-weight: 600;
          font-size: 0.9rem;
          color: #4a5568;
        }

        .form-field input {
          padding: 0.75rem;
          border: 1px solid #cbd5e0;
          border-radius: 6px;
          font-size: 0.95rem;
          font-family: inherit;
          background: white;
          transition: border-color 0.2s;
        }

        .form-field input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .social-media-section {
          margin-top: 1.5rem;
          padding-top: 1.5rem;
          border-top: 1px solid #e2e8f0;
        }

        .subsection-title {
          margin: 0 0 1rem 0;
          font-size: 1rem;
          color: #4a5568;
          font-weight: 600;
        }

        .migration-notice {
          padding: 1rem;
          background: #fffaf0;
          border: 1px solid #f6ad55;
          border-radius: 6px;
          margin-bottom: 1rem;
          font-size: 0.875rem;
          color: #744210;
          line-height: 1.6;
        }

        .migration-notice strong {
          font-weight: 600;
        }

        @media (max-width: 768px) {
          .form-row {
            grid-template-columns: 1fr;
          }

          .form-section {
            padding: 1.5rem;
          }
        }
      `}</style>
    </div>
  );
}

const COLOR_ESTADO = { ok: '#6b7280', cerca: '#b45309', excede: '#b91c1c' };

/** El contador: gris, ámbar desde el 90 % del tope, rojo por encima. */
function estiloContador(estado) {
  return { color: COLOR_ESTADO[estado] || COLOR_ESTADO.ok, fontWeight: estado === 'ok' ? 400 : 600 };
}

function estiloAviso(estado) {
  return {
    margin: '0.35rem 0 0', padding: '0.5rem 0.75rem', borderRadius: '6px', fontSize: '0.9rem',
    color: COLOR_ESTADO[estado], background: estado === 'excede' ? '#fef2f2' : '#fffbeb',
    border: `1px solid ${estado === 'excede' ? '#fecaca' : '#fde68a'}`,
  };
}

/** Bajo cada sección: cuánto le queda, o cuánto no verá el bot. Nada si está lejos del tope. */
function AvisoDeLimite({ id, seccion, pegado, t }) {
  if (seccion.estado === 'ok') return null;
  const clave = seccion.estado === 'cerca' ? 'knowledge.limites.cerca' : (pegado ? 'knowledge.limites.pegado' : 'knowledge.limites.excede');
  return (
    <p id={id} role={seccion.estado === 'excede' ? 'alert' : undefined} style={estiloAviso(seccion.estado)}>
      {t(clave, { max: seccion.max, sobra: seccion.sobra, quedan: seccion.max - seccion.largo })}
    </p>
  );
}
